import {
  FieldDiff,
  FormatError,
} from "../../utils/subscriberSyncUpload/compareRow";
import {
  DraftNote,
  EMPTY_DRAFT_NOTE,
} from "../../components/notes/NoteComposer";

export type WizardStep = "select-scheme" | "upload-file" | "review";

export type RowStatus = "in_sync" | "has_differences" | "not_found" | "invalid";

export type RowDecision = "pending" | "approved" | "declined";

export type UploadRow = {
  id: string;
  rowIndex: number;
  accountNumber: string;
  customerName: string;
  rowStatus: RowStatus;
  // Backend commit status (SubscriberSyncUploadRowStatus): 0 Draft / 1 Success / 2 Failed. Spread
  // from the server row; used to hide the row Delete action once a row has been ledgered (Success),
  // mirroring bulk meter upload's trial-run row actions.
  status?: number;
  diffs: FieldDiff[];
  formatErrors: FormatError[];
  decision: RowDecision;
  // Editable per-row note (from the template's Note column). Becomes a Note
  // anchored to matchedSubscriberId at finalize.
  note: string;
  // The matched Subscriber anchor — non-null for in_sync / has_differences,
  // null for not_found / invalid. Used as the NotesPanel reference id.
  matchedSubscriberId: string | null;
  // Raw CRM payload as persisted server-side (camelCase). Present on rows hydrated
  // from a draft; the Row Detail modal reads the proposed CRM field values from it.
  rowData?: Record<string, unknown>;
  // Current SEQWL ("ledger") values captured at classification, used by the File-vs-Ledger
  // comparison modal. Null for not_found / invalid rows (no matched customer to snapshot).
  seqwlSnapshot?: SeqwlSnapshot | null;
};

// Mirror of the backend SeqwlSnapshot (App/Models/SubscriberSyncUploadRow). Only the fields the
// comparison modal renders are typed here.
export type SeqwlSnapshot = {
  subscriber: {
    name?: string;
    abn?: string;
    acn?: string;
    isActive?: boolean;
    address?: {
      street?: string;
      town?: string;
      region?: string;
      postcode?: string;
      country?: string;
    };
  };
  user: { email?: string; tel?: string } | null;
  // AC-24: per-field record of Zenith self-service edits. `fields` maps a field name
  // (name/abn/acn/isActive/street/town/region/postcode/country, plus email/mobile) to the ISO date the
  // customer last changed THAT field — present only when the field's most recent change was the
  // customer's. The modal surfaces these beside the current SEQWL value for the matching changed field.
  selfService?: {
    fields?: Record<string, string>;
  } | null;
};

export type ReviewFilter = "actionable" | "all";

export type WizardState = {
  step: WizardStep;
  level1ResourceId: string | null;
  uploadId: string | null;
  rows: UploadRow[];
  filter: ReviewFilter;
  // FR-6 session note (body + reportable) entered on the upload step via NoteComposer. Held in
  // wizard state so it survives step navigation; committed to the sync record at finalize.
  sessionNote: DraftNote;
};

export type WizardAction =
  | { type: "select-scheme"; level1ResourceId: string }
  | { type: "back-to-select-scheme" }
  | { type: "trial-run-loaded"; uploadId: string; rows: UploadRow[] }
  | {
      type: "decide-row";
      rowId: string;
      decision: Exclude<RowDecision, "pending">;
    }
  | { type: "update-row-note"; rowId: string; note: string }
  | {
      type: "bulk-decide";
      decision: Exclude<RowDecision, "pending">;
      rowIds?: string[];
    }
  | { type: "remove-row"; rowId: string }
  | { type: "set-filter"; filter: ReviewFilter }
  | { type: "set-session-note"; note: DraftNote }
  | { type: "reset" };

export const initialState: WizardState = {
  step: "select-scheme",
  level1ResourceId: null,
  uploadId: null,
  rows: [],
  filter: "actionable",
  sessionNote: EMPTY_DRAFT_NOTE,
};

export function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "select-scheme":
      return {
        ...state,
        step: "upload-file",
        level1ResourceId: action.level1ResourceId,
      };
    case "back-to-select-scheme":
      return { ...state, step: "select-scheme" };
    case "trial-run-loaded":
      // Also used to return to Review to retry rows that finalized Failed.
      return {
        ...state,
        step: "review",
        uploadId: action.uploadId,
        rows: action.rows,
      };
    case "decide-row":
      return {
        ...state,
        rows: state.rows.map(row =>
          row.id === action.rowId && row.rowStatus === "has_differences"
            ? {
                ...row,
                decision: action.decision,
                // TC-14: a declined row commits nothing, so it must not carry a per-row note.
                note: action.decision === "declined" ? "" : row.note,
              }
            : row,
        ),
      };
    case "update-row-note":
      return {
        ...state,
        rows: state.rows.map(row =>
          row.id === action.rowId ? { ...row, note: action.note } : row,
        ),
      };
    case "bulk-decide": {
      // When rowIds is passed, only flip those (the same subset the request sent) so ledgered
      // has_differences rows on a resumed PartiallyCompleted upload don't visually flip decisions
      // the server refused. Falls back to every has_differences row for callers that don't scope.
      const scoped = action.rowIds ? new Set(action.rowIds) : null;
      return {
        ...state,
        rows: state.rows.map(row =>
          row.rowStatus === "has_differences" &&
          (scoped === null || scoped.has(row.id))
            ? {
                ...row,
                decision: action.decision,
                note: action.decision === "declined" ? "" : row.note,
              }
            : row,
        ),
      };
    }
    case "remove-row":
      return {
        ...state,
        rows: state.rows.filter(row => row.id !== action.rowId),
      };
    case "set-filter":
      return { ...state, filter: action.filter };
    case "set-session-note":
      return { ...state, sessionNote: action.note };
    case "reset":
      return initialState;
  }
}

// Submit is gated on the non-ledgered rows only: already-committed (status === Success) rows are
// excluded from finalize server-side, so a fully-ledgered resume has nothing to submit (disabled),
// and a partial resume only needs its remaining has_differences rows decided.
export function canSubmit(state: WizardState): boolean {
  const pending = state.rows.filter(row => row.status !== 1); // exclude ledgered (Success)
  if (pending.length === 0) return false;
  return pending.every(
    row => row.rowStatus !== "has_differences" || row.decision !== "pending",
  );
}
